import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import Dashboard from "../../../layout/Dashboard";
import { giftcardService } from '../../../services/GiftcardService';
import { ListHead, ListRow, Ticker } from "../../../styles/CrypoAssetStyle";
import { Content, Heading } from "../../../styles/OverviewStyle";
import { RFDesc, RFMain, RFTitle, RFWrap } from "../../../styles/RatesFeesStyle";
import { CloseBtn, H1, Overlay, Panel, Section, TitleRow } from '../../../styles/TransactionDetailStyle';
import { RatesNavigator, TabNavigator } from "../components/Navigator";

type Product = {
   productId: number
   productName: string
   amountsInUSD: number[]
   buyPrices: number[]
}

type Region = {
   countryName: string
   countryCode: string
   flagUrl: string
   products: Product[]
}

type GiftcardType = {
   type: string
   logoUrl: string
   regions: Region[]
}

export default function BuyGiftcardRates() {
   const [data, setData] = useState<GiftcardType[]>([])
   const [loading, setLoading] = useState(true)
   const [open, setOpen] = useState<Record<string, boolean>>({})
   const [selectedProduct, setSelectedProduct] = useState<{ product: Product; region: Region } | null>(null)
   const [buyPercentage, setBuyPercentage] = useState('')
   const [editingPercentage, setEditingPercentage] = useState(false)
   const [percentageValue, setPercentageValue] = useState('')
   const [loadingPercentage, setLoadingPercentage] = useState(true)

   useEffect(() => {
      loadBuyRates()
      loadBuyPercentage()
   }, [])

   const loadBuyRates = async () => {
      try {
         const response = await giftcardService.getBuyRates()
         if (response.status === 'success' && response.data) {
            setData(response.data)
         }
      } catch (error) {
         console.error('Failed to load buy rates:', error)
      } finally {
         setLoading(false)
      }
   }

   const loadBuyPercentage = async () => {
      try {
         const response = await giftcardService.getBuyPercentage()
         if (response.status === 'success' && response.data) {
            setBuyPercentage(response.data.buyPercentage)
         }
      } catch (error) {
         console.error('Failed to load buy percentage:', error)
      } finally {
         setLoadingPercentage(false)
      }
   }

   const startEditPercentage = () => {
      setEditingPercentage(true)
      setPercentageValue(buyPercentage)
   }

   const cancelEditPercentage = () => {
      setEditingPercentage(false)
      setPercentageValue('')
   }

   const savePercentage = async () => {
      try {
         const value = parseFloat(percentageValue)
         if (isNaN(value)) return

         await giftcardService.updateBuyPercentage({ buyPercentage: value })
         await loadBuyPercentage()
         setEditingPercentage(false)
         setPercentageValue('')
      } catch (error) {
         console.error('Failed to update buy percentage:', error)
      }
   }

   const toggle = (k: string) => setOpen(o => ({ ...o, [k]: !o[k] }))

   return (
      <Dashboard title="Rates & Fees / Rates / Buy giftcard" icon="solar:tag-price-linear" active="rates_fees">
         <Content>
            <Heading>Rates & Fees</Heading>

            <TabNavigator active="rates" />

            <RFWrap>
               <RatesNavigator active="buy_giftcard" />

               <RFMain>
                  <div>
                     <RFTitle>Buy giftcard</RFTitle>
                     <RFDesc>
                        These gift card rates are provided by your integrated API partner. They update automatically and are not editable from this dashboard. Any changes must be made directly in your API provider settings
                     </RFDesc>

                     <div className="mt-6 mb-6 p-4 bg-[#F9F9FA] rounded-lg flex items-center justify-between">
                        <div>
                           <div className="text-xs text-[#9C9C9F] mb-1">Buy Percentage</div>
                           <div className="text-sm text-[#101018]">
                              {loadingPercentage ? (
                                 <span className="text-[#9C9C9F]">Loading...</span>
                              ) : editingPercentage ? (
                                 <input
                                    value={percentageValue}
                                    onChange={e => setPercentageValue(e.target.value)}
                                    className="w-[100px] h-8 px-3 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                                    placeholder="0.00"
                                 />
                              ) : (
                                 <span className="font-medium">{buyPercentage}%</span>
                              )}
                           </div>
                        </div>
                        <div className="flex items-center gap-2">
                           {editingPercentage ? (
                              <>
                                 <button onClick={cancelEditPercentage} className="cursor-pointer">
                                    <Icon icon="mdi:close" width={16} className="text-[#DD524D] cursor-pointer" />
                                 </button>
                                 <button onClick={savePercentage} className="cursor-pointer">
                                    <Icon icon="mdi:check" width={16} className="text-[#4CAF50] cursor-pointer" />
                                 </button>
                              </>
                           ) : (
                              <button onClick={startEditPercentage} className="text-[#AF7AD5] text-xs hover:underline cursor-pointer">
                                 Edit
                              </button>
                           )}
                        </div>
                     </div>

                     {loading ? (
                        <div className="flex justify-center py-12">
                           <Icon icon="svg-spinners:ring-resize" width={32} className="text-[#AF7AD5]" />
                        </div>
                     ) : (
                        <>
                           <ListHead style={{ gridTemplateColumns: '1fr 150px' }}>
                              <div>Card</div>
                              <div></div>
                           </ListHead>

                           {data.map(card => (
                              <div key={card.type}>
                                 <ListRow style={{ gridTemplateColumns: '1fr 150px' }}>
                                    <div className="flex items-center gap-3">
                                       <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-[#F1F1F3] overflow-hidden">
                                          <img src={card.logoUrl} alt={card.type} className="w-full h-full object-cover" />
                                       </span>
                                       <span className="text-sm text-[#101018]">{card.type}</span>
                                    </div>
                                    <div className="flex items-center justify-end">
                                       <Icon
                                          icon={open[card.type] ? 'solar:alt-arrow-up-bold' : 'solar:alt-arrow-down-bold'}
                                          width={14}
                                          onClick={() => toggle(card.type)}
                                          className={`cursor-pointer ${card.regions.length > 0 ? 'text-[#AF7AD5]' : 'text-[#CACACC]'}`}
                                       />
                                    </div>
                                 </ListRow>

                                 {open[card.type] && card.regions.length > 0 && (
                                    <div className="mb-3 bg-[#FBFBFB] rounded-b-lg">
                                       {card.regions.map((region) => (
                                          <div key={region.countryCode} className='flex items-center py-3 pr-2'>
                                             <div className="flex-1 flex items-center gap-2 text-[#858589] text-sm pl-8 min-w-0">
                                                <img src={region.flagUrl} alt={region.countryName} className="w-4 h-3 object-cover rounded-sm flex-shrink-0" />
                                                <span className="whitespace-nowrap">{region.countryName}</span>
                                                <Ticker style={{ color: '#6D6D72' }}>{region.countryCode}</Ticker>
                                             </div>
                                             <div className="w-[100px] flex items-center justify-end gap-4 pl-4 flex-shrink-0">
                                                {region.products.map(product => (
                                                   product.amountsInUSD.length > 0 ? (
                                                      <button
                                                         key={product.productId}
                                                         className="text-[#AF7AD5] text-xs hover:underline cursor-pointer"
                                                         onClick={() => setSelectedProduct({ product, region })}
                                                      >
                                                         View
                                                      </button>
                                                   ) : (
                                                      <span key={product.productId} className="text-[#B3B3B6] text-xs">No rates</span>
                                                   )
                                                ))}
                                             </div>
                                          </div>
                                       ))}
                                    </div>
                                 )}
                              </div>
                           ))}
                        </>
                     )}
                  </div>
               </RFMain>
            </RFWrap>
         </Content>

         {selectedProduct && (
            <ProductOverlay
               product={selectedProduct.product}
               region={selectedProduct.region}
               onClose={() => setSelectedProduct(null)}
            />
         )}
      </Dashboard>
   )
}

function ProductOverlay({ product, region, onClose }: { product: Product; region: Region; onClose: () => void }) {
   useEffect(() => {
      function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
      window.addEventListener('keydown', onEsc)
      return () => window.removeEventListener('keydown', onEsc)
   }, [onClose])

   return (
      <Overlay onClick={onClose}>
         <Panel onClick={e => e.stopPropagation()}>
            <TitleRow>
               <H1>Product Details</H1>
               <CloseBtn onClick={onClose}><Icon icon="material-symbols:close" width={20} /></CloseBtn>
            </TitleRow>

            <Section>
               <div className='text-[12px] tracking-tight text-[#9C9C9F] mb-2'>Product Name</div>
               <div className='text-sm text-[#101018] font-medium'>{product.productName}</div>
            </Section>

            <Section>
               <div className='text-[12px] tracking-tight text-[#9C9C9F] mb-2'>Region</div>
               <div className='flex items-center gap-2'>
                  <img src={region.flagUrl} alt={region.countryName} className="w-5 h-4 object-cover rounded-sm" />
                  <span className='text-sm text-[#101018]'>{region.countryName}</span>
               </div>
            </Section>

            <Section>
               <div className='text-[12px] tracking-tight text-[#9C9C9F] mb-3'>Available Amounts & Prices</div>
               <div className='space-y-2'>
                  {product.amountsInUSD.map((amount, idx) => (
                     <div key={idx} className='flex items-center justify-between py-2 px-3 bg-[#F9F9FA] rounded-lg'>
                        <span className='text-sm text-[#6D6D72]'>${amount} USD</span>
                        <span className='text-sm text-[#101018] font-medium'>₦{product.buyPrices[idx]?.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                     </div>
                  ))}
               </div>
            </Section>
         </Panel>
      </Overlay>
   )
}
